import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ServiceCard, SectionHeading, TestimonialsSection } from "@/components/Cards";
import { Button } from "@/components/ui/button";
import { MapPin, Clock, Info, ArrowLeft, ArrowRight, Landmark, Loader2, Navigation, BedDouble, ShieldCheck, Star, Images } from "lucide-react";

export default function MandirDetails() {
  const { id } = useParams();
  const [m, setM] = useState(null);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get(`/mandirs/${id}`).then((r) => setM(r.data)).catch(() => setM(false));
    api.get("/testimonials").then((r) => setTestimonials(r.data));
  }, [id]);

  if (m === null) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (m === false) return <div className="min-h-[50vh] grid place-items-center"><p>Mandir not found. <Link to="/mandirs" className="text-primary underline">Back to Mandirs</Link></p></div>;

  return (
    <div data-testid="mandir-detail-page">
      <section className="relative h-[46vh] min-h-[340px] overflow-hidden">
        <img src={m.image} alt={m.name} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/20" />
        <div className="absolute inset-0 flex flex-col justify-end">
          <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto w-full pb-10 text-white">
            <Link to="/mandirs" className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white mb-4"><ArrowLeft className="h-4 w-4" /> All Mandirs</Link>
            <span className="inline-block text-[11px] font-medium px-2.5 py-1 rounded-full bg-primary mb-3">{m.deity}</span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold">{m.name}</h1>
            <p className="mt-2 flex items-center gap-1.5 text-white/90"><MapPin className="h-4 w-4" /> {m.location}</p>
            <Link to="/divine-bless" className="inline-block mt-5">
              <Button data-testid="mandir-book-cta" className="rounded-full h-11 px-7 bg-primary hover:bg-primary/90 text-primary-foreground">Book Worship <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-12 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <div>
            <h2 className="font-serif text-2xl font-semibold flex items-center gap-2"><Landmark className="h-6 w-6 text-accent" /> About the Mandir</h2>
            <p className="mt-3 text-muted-foreground leading-relaxed">{m.history}</p>
          </div>

          {m.gallery?.length > 0 && (
            <div data-testid="mandir-gallery">
              <h2 className="font-serif text-2xl font-semibold flex items-center gap-2 mb-4"><Images className="h-6 w-6 text-accent" /> Gallery</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {m.gallery.map((img, i) => (
                  <div key={i} data-testid={`gallery-img-${i}`} className="overflow-hidden rounded-xl border border-border group aspect-[4/3]">
                    <img src={img} alt={`${m.name} ${i + 1}`} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-card border border-border p-5 hover-lift" data-testid="how-to-reach">
              <h3 className="font-serif text-lg font-semibold flex items-center gap-2 mb-2"><Navigation className="h-5 w-5 text-accent" /> How to Reach</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{m.how_to_reach || "Reachable by air, rail and road. Local autos and taxis are readily available."}</p>
            </div>
            <div className="rounded-2xl bg-card border border-border p-5 hover-lift" data-testid="where-to-stay">
              <h3 className="font-serif text-lg font-semibold flex items-center gap-2 mb-2"><BedDouble className="h-5 w-5 text-accent" /> Where to Stay</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{m.where_to_stay || "A range of dharamshalas, budget lodges and hotels are available near the temple."}</p>
            </div>
          </div>
        </div>
        <aside className="space-y-4">
          <div className="rounded-2xl bg-card border border-border p-5 space-y-3">
            <div className="flex items-start gap-2"><Clock className="h-4 w-4 text-accent mt-1" /><div><p className="text-xs uppercase tracking-wide text-muted-foreground">Timings</p><p className="text-sm font-medium">{m.timings}</p></div></div>
            <div className="flex items-start gap-2"><Info className="h-4 w-4 text-accent mt-1" /><div><p className="text-xs uppercase tracking-wide text-muted-foreground">Important Information</p><p className="text-sm">{m.important_info}</p></div></div>
          </div>
        </aside>
      </section>

      {m.purohits?.length > 0 && (
        <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto pb-4" data-testid="mandir-purohits">
          <SectionHeading eyebrow="Vidhi Karta" title="Purohits at this Mandir" subtitle="Meet the verified priests who perform worships and rituals here." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {m.purohits.map((p) => (
              <div key={p.id} data-testid={`mandir-purohit-${p.id}`} className="rounded-2xl bg-card border border-border p-5 flex gap-4 hover-lift hover:border-accent">
                <img src={p.image} alt={p.name} className="h-16 w-16 rounded-full object-cover shrink-0" />
                <div className="min-w-0">
                  <p className="font-serif text-lg font-semibold leading-tight flex items-center gap-1.5">{p.name} <ShieldCheck className="h-4 w-4 text-emerald-500 shrink-0" /></p>
                  <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                    {p.rating_count > 0 && <span className="flex items-center gap-0.5 text-accent"><Star className="h-3 w-3 fill-accent" /> {p.rating} ({p.rating_count})</span>}
                    <span>{p.experience} yrs · {p.certifications}</span>
                  </p>
                  <p className="text-sm text-muted-foreground mt-2 line-clamp-3">{p.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-12">
        <SectionHeading eyebrow="Sevaayein" title="Worships & Rituals available here" subtitle="Book an authentic worship at this Mandir, performed by its verified purohits." />
        {m.services?.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {m.services.map((s) => <ServiceCard key={s.id} s={s} mandirId={m.id} />)}
          </div>
        ) : <p className="text-muted-foreground">No worships listed yet for this Mandir.</p>}
      </section>

      <TestimonialsSection testimonials={testimonials} />

      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 lg:py-20">
        <div className="relative rounded-3xl overflow-hidden p-8 sm:p-12 flex flex-col md:flex-row items-center justify-between gap-6 min-h-[240px] border border-border">
          <img src="https://images.unsplash.com/photo-1605302977545-3a09913be1dd?crop=entropy&cs=srgb&fm=jpg&q=85" alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/70 to-black/35" />
          <div className="relative text-white">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold">Ready to offer your worship at {m.name}?</h2>
            <p className="mt-2 text-white/90 max-w-lg">Let a verified Purohit perform your chosen worship or ritual with complete vidhi.</p>
          </div>
          <Link to="/divine-bless" className="relative"><Button data-testid="mandir-detail-book-cta" className="rounded-full h-12 px-7 bg-white text-primary hover:bg-white/90">Book Worship <ArrowRight className="ml-1.5 h-4 w-4" /></Button></Link>
        </div>
      </section>
    </div>
  );
}
